import type { CatalogSort } from './catalog';
import { Card, CardPrinting, SimplifiedCard } from './catalog';
import type {
  InventoryCondition,
  InventoryEdition,
  LanguageInfo,
} from './inventory-values';

export * from './inventory-values';

export interface InventoryItem {
  id: string;
  printing: CardPrinting & { card: SimplifiedCard };
  language: LanguageInfo;
  condition: InventoryCondition;
  edition: InventoryEdition | null;
  observation?: string | null;
  quantity: number;
  price: string;
  originalPrice?: string;
  promotionEndsAt?: string;
  active: boolean;
}

export type EditableInventoryItem = InventoryItem;

export interface OwnerInventoryItem extends InventoryItem {
  createdAt: string;
  updatedAt: string;
}

export interface AdminInventoryItem extends OwnerInventoryItem {
  store: { id: string; name: string; slug: string };
}

export interface InventoryInput {
  printingId: string;
  languageId: string;
  condition: InventoryCondition;
  edition?: InventoryEdition | null;
  observation?: string | null;
  quantity: number;
  price: number;
  active?: boolean;
}

export interface InventoryImportRow {
  cardName: string;
  printingCode?: string;
  printingVersion?: string;
  rarity?: string;
  rawRarity?: string;
  languageCode: string;
  condition: InventoryCondition;
  edition?: InventoryEdition | null;
  quantity: number;
  price: number;
  obs?: string;
}

export const INVENTORY_IMPORT_AMBIGUITY_REASON = {
  MULTIPLE_PRINTINGS_MATCH: 'MULTIPLE_PRINTINGS_MATCH',
  MULTIPLE_RARITIES_IN_COLLECTION: 'MULTIPLE_RARITIES_IN_COLLECTION',
  RARITY_NOT_FOUND_IN_COLLECTION: 'RARITY_NOT_FOUND_IN_COLLECTION',
} as const;

export type InventoryImportAmbiguityReason =
  (typeof INVENTORY_IMPORT_AMBIGUITY_REASON)[keyof typeof INVENTORY_IMPORT_AMBIGUITY_REASON];

interface InventoryImportMatchBase {
  index: number;
  card: Pick<Card, 'id' | 'nameEn' | 'namePt' | 'slug'> & {
    primaryArtwork: Pick<
      NonNullable<CardPrinting['artwork']>,
      'imageUrl'
    > | null;
  };
  row: InventoryImportRow;
  candidates: CardPrinting[];
  language?: { id: string; name: string; code: string };
}

export type InventoryImportMatch = InventoryImportMatchBase &
  (
    | { status: 'exact' | 'not_found' }
    | {
        status: 'ambiguous';
        ambiguityReason: InventoryImportAmbiguityReason;
      }
  );

export interface OwnerInventoryFilters {
  search?: string;
  gameCode?: string;
  collectionId?: string;
  rarityId?: string;
  condition?: InventoryCondition;
  minPrice?: string;
  maxPrice?: string;
  status?: 'all' | 'active' | 'paused';
  page?: number;
  cursor?: string;
  limit?: number;
  sort?: CatalogSort;
}

export interface OwnerInventoryPage {
  items: OwnerInventoryItem[];
  nextCursor?: string;
  totalItems: number;
}

export interface BulkPriceUpdateFilter {
  collectionId?: string;
  cardId?: string;
  printingId?: string;
  rarityId?: string;
  languageId?: string;
  condition?: InventoryCondition;
  status?: 'all' | 'active' | 'paused';
  search?: string;
  itemIds?: string[];
}

export interface BulkPriceUpdateAction {
  adjustmentType: 'percentage' | 'fixed_delta' | 'fixed_price';
  adjustmentValue: number;
}

export interface BulkPricePreviewItem {
  id: string;
  item: OwnerInventoryItem;
  currentPrice: string;
  newPrice: string;
  delta: string;
  percentageChange: number;
  salesCount48h: number;
  avgPrice48h: string | null;
}

export interface BulkPricePreview {
  totalItems: number;
  totalSalesCount48h: number;
  avgSalesPrice48h: string | null;
  items: BulkPricePreviewItem[];
}

export interface BulkPriceApplyResult {
  updatedCount: number;
  items: OwnerInventoryItem[];
}

export interface AssistedImportRequest {
  id: string;
  accountId: string;
  accountName: string;
  accountEmail: string;
  accountPhone?: string;
  storeId?: string;
  storeName?: string;
  storeSlug?: string;
  source: string;
  status: 'pending' | 'contacted' | 'completed' | 'cancelled';
  notes?: string;
  contactedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssistedImportFilters {
  status?: 'pending' | 'contacted' | 'completed' | 'cancelled';
  search?: string;
}

export interface PublicInventoryPage {
  items: InventoryItem[];
  nextCursor?: string;
  totalItems: number;
}

export interface InventoryImportBatchReceipt {
  operationId: string;
  batchIndex: number;
  total: number;
  processed: number;
  createdRows: number;
  updatedRows: number;
  completed: boolean;
}

export interface DurableInventoryBatchInput {
  operationId: string;
  batchIndex: number;
  items: InventoryInput[];
}
